import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import usePunchPointStore from '../store/punchPointStore';
import useUIStore from '../store/uiStore';
import useAuthStore from '../store/authStore';
import { getDepartments } from '../services/employeeService';
import { getProjects } from '../services/projectService';
import { formatDateDDMMYY } from '../utils/formatDate';
import ScrollX from '../components/ScrollX';

const statusColors = {
  open: 'bg-gray-200 text-gray-800',
  completed: 'bg-blue-100 text-blue-800',
  verified: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const severityColors = {
  low: 'text-green-600',
  medium: 'text-amber-600',
  high: 'text-red-600',
};

// Roles allowed to verify/reject any entry, overriding the normal
// "only the creator can verify" rule - must match VERIFY_OVERRIDE_ROLES
// in punch_point_routes.py. This only controls whether the button shows;
// the backend enforces the real rule regardless of what the UI does.
const VERIFY_OVERRIDE_ROLES = ['CEO'];

const PunchPointListPage = () => {
  const {
    punchPoints,
    isLoading,
    error,
    filters,
    setFilters,
    fetchPunchPoints,
    completePunchPoint,
    verifyPunchPoint,
    deletePunchPoint,
    archivePunchPoint,
    unarchivePunchPoint,
  } = usePunchPointStore();
  const { user } = useAuthStore();
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    setFilters({ archived: showArchived });
    fetchPunchPoints({ archived: showArchived });
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    getProjects().then((res) => setProjects(res.data.projects || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value || null;
    setFilters({ [field]: value });
    fetchPunchPoints({ [field]: value });
  };

  const canComplete = (pp) => {
    if (!user) return false;
    if (VERIFY_OVERRIDE_ROLES.includes(user.role)) return true;
    if (pp.assigned_to_supervisor_id) return user.id === pp.assigned_to_supervisor_id;
    if (pp.assigned_to_dept_id) {
      const dept = departments.find((d) => d.id === pp.assigned_to_dept_id);
      if (dept && user.id === dept.hod_id) return true;
      // Department.hod_id isn't set for every department today - fall back
      // to "current user holds role=HOD in that department", mirroring the
      // backend's own fallback in _assert_can_complete.
      return user.role === 'HOD' && user.department_id === pp.assigned_to_dept_id;
    }
    return false;
  };

  const canVerify = (pp) => !!user && (user.id === pp.created_by_id || VERIFY_OVERRIDE_ROLES.includes(user.role));

  const canDelete = (pp) =>
    !!user &&
    pp.status !== 'verified' &&
    (user.id === pp.created_by_id || VERIFY_OVERRIDE_ROLES.includes(user.role) || user.role === 'HOD');

  // Archive/unarchive: same creator-or-CEO-or-any-HOD rule as delete, but
  // only ever applies to verified entries (nothing else is "done" enough
  // to tuck away).
  const canArchive = (pp) =>
    !!user &&
    pp.status === 'verified' &&
    (user.id === pp.created_by_id || VERIFY_OVERRIDE_ROLES.includes(user.role) || user.role === 'HOD');

  const handleComplete = async (id) => {
    const summary = window.prompt('Completion summary:');
    if (summary === null) return;
    const result = await completePunchPoint(id, summary);
    if (result.success) {
      showToast('Marked as completed', 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleDelete = async (pp) => {
    if (!window.confirm(`Delete "${pp.issue_description}"? This can't be undone.`)) return;
    const result = await deletePunchPoint(pp.id);
    if (result.success) {
      showToast('Entry deleted', 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleArchive = async (pp) => {
    const result = await archivePunchPoint(pp.id);
    if (result.success) {
      showToast('Entry archived', 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleUnarchive = async (pp) => {
    const result = await unarchivePunchPoint(pp.id);
    if (result.success) {
      showToast('Entry restored', 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleVerify = async (id) => {
    const notes = window.prompt('Verification notes (optional):') || '';
    const result = await verifyPunchPoint(id, notes);
    if (result.success) {
      showToast(
        result.data.delay_detected
          ? `Verified — delay of ${result.data.days_late} day(s) recorded`
          : 'Verified — on time',
        result.data.delay_detected ? 'warning' : 'success'
      );
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Task and Issues Register</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              onChange={handleFilterChange('entry_type')}
              defaultValue=""
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All entries</option>
              <option value="task">Tasks</option>
              <option value="issue">Issues</option>
            </select>
            <select
              onChange={handleFilterChange('status_filter')}
              defaultValue=""
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="completed">Completed</option>
              <option value="verified">Verified</option>
            </select>
            <select
              onChange={handleFilterChange('severity')}
              defaultValue=""
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <select
              onChange={handleFilterChange('project_id')}
              defaultValue=""
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select
              onChange={handleFilterChange('dept_id')}
              defaultValue=""
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <button
              onClick={() => setShowArchived((v) => !v)}
              className={`text-sm font-semibold px-3 py-2 rounded border ${
                showArchived
                  ? 'bg-white text-komal-blue border-white'
                  : 'text-white border-white/40 hover:bg-white/10'
              }`}
            >
              {showArchived ? '← Active entries' : 'Archived'}
            </button>
            <Link
              to="/punch-points/import"
              className="text-sm font-semibold text-white hover:underline px-2"
            >
              Import
            </Link>
            <Link
              to="/punch-points/create"
              className="bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded"
            >
              + New Entry
            </Link>
          </div>
        </div>

        <div className="glass-card">
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : punchPoints.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">
              {showArchived ? 'No archived entries.' : 'No entries found.'}
            </p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">Entry</th>
                  <th className="px-5 py-2 font-medium">Type</th>
                  <th className="px-5 py-2 font-medium">Issue</th>
                  <th className="px-5 py-2 font-medium">Severity</th>
                  <th className="px-5 py-2 font-medium">Issue Date</th>
                  <th className="px-5 py-2 font-medium">Assigned By</th>
                  <th className="px-5 py-2 font-medium">Assigned To</th>
                  <th className="px-5 py-2 font-medium">Due</th>
                  <th className="px-5 py-2 font-medium">Current Delay</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 font-medium">Completed</th>
                  <th className="px-5 py-2 font-medium">Verified</th>
                  <th className="px-5 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {punchPoints.map((pp) => (
                  <tr key={pp.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${pp.entry_type === 'task' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>
                        {pp.entry_type === 'task' ? 'Task' : 'Issue'}
                      </span>
                    </td>
                    <td className="px-5 py-3 capitalize text-gray-500">{pp.type?.replace(/_/g, ' ')}</td>
                    <td className="px-5 py-3 max-w-xs truncate">{pp.issue_description}</td>
                    <td className={`px-5 py-3 capitalize font-medium ${severityColors[pp.severity]}`}>
                      {pp.severity}
                    </td>
                    <td className="px-5 py-3 text-gray-500">{formatDateDDMMYY(pp.created_date) || '—'}</td>
                    <td className="px-5 py-3 text-gray-500">{pp.created_by_name || '—'}</td>
                    <td className="px-5 py-3 text-gray-500">
                      {pp.assigned_to_supervisor_name || pp.assigned_to_dept_name || '—'}
                    </td>
                    <td className="px-5 py-3">{formatDateDDMMYY(pp.expected_completion_date)}</td>
                    <td className="px-5 py-3">
                      {pp.status !== 'verified' && pp.current_delay_days > 0 ? (
                        <span className="text-red-600 font-semibold">{pp.current_delay_days} day{pp.current_delay_days === 1 ? '' : 's'}</span>
                      ) : pp.status === 'verified' && pp.days_late > 0 ? (
                        <span className="text-amber-600 font-semibold">{pp.days_late} day{pp.days_late === 1 ? '' : 's'} (final)</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${statusColors[pp.status]}`}>
                        {pp.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">
                      {pp.completed_by_name ? (
                        <>{pp.completed_by_name}<br />{formatDateDDMMYY(pp.completed_date)}</>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">
                      {pp.verified_by_name ? (
                        <>{pp.verified_by_name}<br />{formatDateDDMMYY(pp.verified_date)}</>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 space-x-2 whitespace-nowrap">
                      {pp.status === 'open' && canComplete(pp) && (
                        <button
                          onClick={() => handleComplete(pp.id)}
                          className="text-komal-blue hover:underline text-xs font-semibold"
                        >
                          Mark Complete
                        </button>
                      )}
                      {pp.status === 'completed' && canVerify(pp) && (
                        <button
                          onClick={() => handleVerify(pp.id)}
                          className="text-green-700 hover:underline text-xs font-semibold"
                        >
                          Verify
                        </button>
                      )}
                      {showArchived ? (
                        canArchive(pp) && (
                          <button
                            onClick={() => handleUnarchive(pp)}
                            className="text-komal-blue hover:underline text-xs font-semibold"
                          >
                            Unarchive
                          </button>
                        )
                      ) : (
                        canArchive(pp) && (
                          <button
                            onClick={() => handleArchive(pp)}
                            className="text-gray-500 hover:underline text-xs font-semibold"
                          >
                            Archive
                          </button>
                        )
                      )}
                      {canDelete(pp) && (
                        <button
                          onClick={() => handleDelete(pp)}
                          className="text-red-600 hover:underline text-xs font-semibold"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default PunchPointListPage;
